import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { iamApi } from '@/api'
import type { CurrentUser } from '@/api/types'
import { isAuthDisabled } from '@/auth/keycloak'

/**
 * Who is signed in and what they may do.
 *
 * The permission set comes from the service, not the token, and it is what the
 * navigation and every guarded control read. The server enforces the same
 * permissions independently - hiding a button is a courtesy, not a control.
 */
export const useSessionStore = defineStore('session', () => {
  const user = ref<CurrentUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const permissions = computed(() => new Set(user.value?.permissions ?? []))
  const roles = computed(() => user.value?.roles ?? [])
  const displayName = computed(() => user.value?.displayName ?? 'Signed out')

  const initials = computed(() => {
    const name = user.value?.displayName?.trim()
    if (!name) return '?'
    const parts = name.split(/\s+/)
    return (parts.length > 1
      ? parts[0][0] + parts[parts.length - 1][0]
      : name.slice(0, 2)).toUpperCase()
  })

  function can(permission: string): boolean {
    // '*' is only ever granted by the auth-disabled development fallback.
    return permissions.value.has('*') || permissions.value.has(permission)
  }

  /** True when the user holds at least one of these. */
  function canAny(...required: string[]): boolean {
    return required.some(can)
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      user.value = await iamApi.me()
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Could not load your profile'
      if (isAuthDisabled()) {
        // Local development with security off: assume full access so the portal
        // is navigable without a Keycloak to sign in against.
        user.value = {
          id: 0,
          username: 'local',
          email: 'local@example.com',
          displayName: 'Local development',
          status: 'ACTIVE',
          roles: ['SUPER_ADMIN'],
          permissions: ['*'],
        }
      }
    } finally {
      loading.value = false
    }
  }

  return { user, loading, error, permissions, roles, displayName, initials, can, canAny, load }
})
